import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
  AccountBalance as BankIcon
} from '@mui/icons-material';
import api from '../services/api';

const EmployeeProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loggedIn = localStorage.getItem('employeeLoggedIn');
    if (!loggedIn) {
      navigate('/employee/login');
      return;
    }
    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/employee/profile');
      setProfile(response.data);
      setFormData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Use stored data as fallback
      const storedData = localStorage.getItem('employeeData');
      if (storedData) {
        const data = JSON.parse(storedData);
        setProfile(data);
        setFormData(data);
      }
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await api.put('/employee/profile', formData);
      setProfile(response.data);
      setEditMode(false);
      setSuccess('Profile updated successfully!');
      // Update stored data
      localStorage.setItem('employeeData', JSON.stringify(response.data));
    } catch (error) {
      setError('Failed to update profile. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => navigate('/employee/dashboard')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            My Profile
          </Typography>
          <Button 
            color="inherit" 
            startIcon={editMode ? <SaveIcon /> : <EditIcon />}
            onClick={editMode ? handleSave : () => setEditMode(true)}
          >
            {editMode ? 'Save' : 'Edit'}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 10, pb: 3 }}>
        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Basic Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: '#1976d2' }} />
            Personal Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Personal Code"
                name="personal_code"
                value={formData.personal_code || ''}
                disabled
                variant="filled"
                InputProps={{
                  startAdornment: <BadgeIcon sx={{ mr: 1, color: '#666' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                value={formData.date_of_birth ? new Date(formData.date_of_birth).toLocaleDateString() : ''}
                disabled
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nationality"
                name="nationality"
                value={formData.nationality || ''}
                disabled
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label={formData.status === 'active' ? 'Active' : 'Inactive'}
                    color={formData.status === 'active' ? 'success' : 'default'}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Contact Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ mr: 1, color: '#1976d2' }} />
            Contact Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: '#666' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: '#666' }} />
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Address Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 1, color: '#1976d2' }} />
            Address
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                name="address_street"
                value={formData.address_street || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                name="address_city"
                value={formData.address_city || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Postal Code"
                name="address_postal_code"
                value={formData.address_postal_code || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Country"
                name="address_country"
                value={formData.address_country || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Banking Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BankIcon sx={{ mr: 1, color: '#1976d2' }} />
            Banking Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Account (IBAN)"
                name="bank_account"
                value={formData.bank_account || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bank_name"
                value={formData.bank_name || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Emergency Contact */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Emergency Contact
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name"
                name="emergency_contact_name"
                value={formData.emergency_contact_name || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                name="emergency_contact_phone"
                value={formData.emergency_contact_phone || ''}
                onChange={handleChange}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Action Buttons */}
        {editMode && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={() => {
                setFormData(profile);
                setEditMode(false);
                setError('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              sx={{ bgcolor: '#1976d2' }}
            >
              Save Changes
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default EmployeeProfile;
